'use client';
import { prepareCaptchaBody } from '@eliadmin/core/src/api/recaptcha-client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';

import Image from 'next/image';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  ArrowLeft,
  ArrowRight,
  Box,
  Check,
  ChevronRight,
  CircleUserRound,
  CloudCheck,
  DraftingCompass,
  FileOutput,
  FolderClock,
  LogIn,
  Menu,
  PackageOpen,
  Rotate3D,
  Save,
  Send,
  Settings2,
  Sparkles,
  X,
} from 'lucide-react';
import { ConfiguratorAuthDialog } from './configurator-auth-dialog';
import {
  DEFAULT_CONFIG,
  HANDLE_DEPTH_CONTROL_MAX_MM,
  calculateEstimate,
  getDefaultConfiguratorConfig,
  getMaterial,
  getProduct,
  setConfiguratorCatalog,
} from './configurator-data';
import { GeneratedDoorPreview } from './configurator-preview-3d';
import { normalizeDoorConfig } from './door-model';
import {
  ConfigurationStep,
  DocumentsStep,
  MachineStep,
  ProductStep,
  SendStep,
  SuccessScreen,
  formatCurrency,
} from './configurator-steps';
import type {
  ConfiguratorState,
  ConfiguratorCatalog,
  ConfiguratorUser,
  GeneratedOutputs,
  PriceEstimate,
} from './configurator-types';
import {
  canContinueStep,
  canEnterStep,
  canSubmit,
  createEmptyOutputs,
  getContinueHint,
  migrateStoredWorkflowStep,
  parseStoredOutputs,
  parseStoredUser,
  WORKFLOW_SCHEMA_VERSION,
} from './configurator-workflow';
import styles from './configurator.module.css';

const DRAFT_KEY = 'bdv-configurator-draft-v4';
const USER_KEY = 'bdv-configurator-user-v1';
const DRAFT_SAVE_DELAY_MS = 220;
const HERO_PREVIEW_CONFIG = normalizeDoorConfig({
  ...DEFAULT_CONFIG,
  materialId: 'aluminium',
  finishId: 'technical',
});

const FLOW_STEPS = [
  { id: 1, label: 'Výrobok', short: 'Výrobok', icon: PackageOpen },
  { id: 2, label: 'Stroj', short: 'Stroj', icon: Settings2 },
  { id: 3, label: 'Konfigurácia', short: '3D', icon: Rotate3D },
  { id: 4, label: 'Dokumenty a TCN', short: 'PDF / TCN', icon: DraftingCompass },
  { id: 5, label: 'Odoslanie', short: 'Odoslať', icon: Send },
] as const;

type Screen = 'landing' | 'flow' | 'success';
type PendingAuthIntent = 'new' | 'resume' | 'login';

function persistDraft(config: ConfiguratorState, outputs: GeneratedOutputs, currentStep: number) {
  window.localStorage.setItem(
    DRAFT_KEY,
    JSON.stringify({
      config,
      outputs,
      currentStep,
      workflowVersion: WORKFLOW_SCHEMA_VERSION,
      savedAt: new Date().toISOString(),
    }),
  );
}

export function ConfiguratorPage({
  locale = 'sk',
  catalog,
}: {
  locale?: string;
  catalog?: ConfiguratorCatalog | null;
}) {
  const catalogInstalled = useRef(false);
  if (!catalogInstalled.current) {
    setConfiguratorCatalog(catalog);
    catalogInstalled.current = true;
  }
  const editorDefaultConfigRef = useRef<ConfiguratorState | null>(null);
  if (!editorDefaultConfigRef.current) {
    editorDefaultConfigRef.current = limitHandleDepthForEditor(getDefaultConfiguratorConfig());
  }
  const editorDefaultConfig = editorDefaultConfigRef.current;
  const [screen, setScreen] = useState<Screen>('landing');
  const [currentStep, setCurrentStep] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);
  const [config, setConfig] = useState<ConfiguratorState>(() => editorDefaultConfig);
  const [outputs, setOutputs] = useState<GeneratedOutputs>(createEmptyOutputs);
  const [user, setUser] = useState<ConfiguratorUser | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [mobileStepsOpen, setMobileStepsOpen] = useState(false);
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [pendingAuthIntent, setPendingAuthIntent] = useState<PendingAuthIntent>('login');
  const submissionInFlight = useRef(false);
  const latestDraftRef = useRef({ config, outputs, currentStep, screen });

  const estimate = useMemo(() => calculateEstimate(config), [config]);
  const progress = (currentStep / FLOW_STEPS.length) * 100;

  useEffect(() => {
    document.body.classList.add('bdv-configurator-active');

    const storedUser = window.localStorage.getItem(USER_KEY);
    const parsedUser = parseStoredUser(storedUser);
    if (parsedUser) {
      setUser(parsedUser);
    } else if (storedUser) {
      window.localStorage.removeItem(USER_KEY);
    }

    const storedDraft = window.localStorage.getItem(DRAFT_KEY);
    try {
      if (storedDraft) {
        const parsed: unknown = JSON.parse(storedDraft);
        if (!isRecord(parsed)) throw new Error('Invalid draft');
        const storedConfig = isRecord(parsed.config) ? parsed.config : {};
        const handle = isRecord(storedConfig.handle) ? storedConfig.handle : {};
        const edgeTreatment = isRecord(storedConfig.edgeTreatment)
          ? storedConfig.edgeTreatment
          : {};
        const stockAllowanceMm = isRecord(storedConfig.stockAllowanceMm)
          ? storedConfig.stockAllowanceMm
          : {};
        const normalized = limitHandleDepthForEditor(
          normalizeDoorConfig({
            ...editorDefaultConfig,
            ...storedConfig,
            handle: { ...editorDefaultConfig.handle, ...handle },
            edgeTreatment: { ...editorDefaultConfig.edgeTreatment, ...edgeTreatment },
            stockAllowanceMm: { ...editorDefaultConfig.stockAllowanceMm, ...stockAllowanceMm },
          } as ConfiguratorState),
        );
        const parsedOutputs = parseStoredOutputs(parsed.outputs);
        const rawStep =
          typeof parsed.currentStep === 'number' && Number.isFinite(parsed.currentStep)
            ? Math.trunc(parsed.currentStep)
            : 1;
        const storedWorkflowVersion =
          typeof parsed.workflowVersion === 'number' && Number.isFinite(parsed.workflowVersion)
            ? Math.trunc(parsed.workflowVersion)
            : 1;
        let savedStep = migrateStoredWorkflowStep(rawStep, storedWorkflowVersion);
        while (savedStep > 1 && !canEnterStep(savedStep, normalized, parsedOutputs)) {
          savedStep -= 1;
        }
        setConfig(normalized);
        setOutputs(parsedOutputs);
        setCurrentStep(savedStep);
        setMaxVisitedStep(savedStep);
        setDraftAvailable(true);
      }
    } catch {
      window.localStorage.removeItem(DRAFT_KEY);
    }

    return () => document.body.classList.remove('bdv-configurator-active');
  }, [editorDefaultConfig]);

  useEffect(() => {
    if (screen !== 'flow') return;
    setDraftAvailable(true);
    setMaxVisitedStep(previous => Math.max(previous, currentStep));
    const saveTimer = window.setTimeout(() => {
      persistDraft(config, outputs, currentStep);
    }, DRAFT_SAVE_DELAY_MS);

    return () => window.clearTimeout(saveTimer);
  }, [config, currentStep, outputs, screen]);

  useEffect(() => {
    latestDraftRef.current = { config, outputs, currentStep, screen };
  }, [config, currentStep, outputs, screen]);

  useEffect(() => {
    const flushDraft = () => {
      const latest = latestDraftRef.current;
      if (latest.screen === 'flow') {
        persistDraft(latest.config, latest.outputs, latest.currentStep);
      }
    };
    window.addEventListener('pagehide', flushDraft);
    return () => window.removeEventListener('pagehide', flushDraft);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep, screen]);

  const update = (patch: Partial<ConfiguratorState>) => {
    setSubmitError(null);
    const manufacturingKeys: Array<keyof ConfiguratorState> = [
      'productId',
      'machineId',
      'materialId',
      'widthMm',
      'heightMm',
      'thicknessMm',
      'quantity',
      'handle',
      'edgeTreatment',
      'stockAllowanceMm',
      'finishId',
      'includeQuote',
      'includeDrawing',
      'includeConfigFile',
      'includeTcnFile',
    ];
    if (manufacturingKeys.some(key => key in patch)) setOutputs(createEmptyOutputs());
    setConfig(previous =>
      normalizeDoorConfig({
        ...previous,
        ...patch,
        handle: patch.handle ? { ...previous.handle, ...patch.handle } : previous.handle,
        edgeTreatment: patch.edgeTreatment
          ? { ...previous.edgeTreatment, ...patch.edgeTreatment }
          : previous.edgeTreatment,
        stockAllowanceMm: patch.stockAllowanceMm
          ? { ...previous.stockAllowanceMm, ...patch.stockAllowanceMm }
          : previous.stockAllowanceMm,
      }),
    );
  };

  const resetNewConfiguration = (nextUser: ConfiguratorUser | null) => {
    setConfig({
      ...editorDefaultConfig,
      handle: { ...editorDefaultConfig.handle },
      edgeTreatment: { ...editorDefaultConfig.edgeTreatment },
      stockAllowanceMm: { ...editorDefaultConfig.stockAllowanceMm },
      customerName: nextUser?.name ?? '',
      customerEmail: nextUser?.email ?? '',
      company: nextUser?.company ?? '',
    });
    setOutputs(createEmptyOutputs());
    setCurrentStep(1);
    setMaxVisitedStep(1);
    setSubmitError(null);
  };

  const openAuth = (intent: PendingAuthIntent) => {
    setPendingAuthIntent(intent);
    setAuthOpen(true);
  };

  const startNew = () => {
    if (user) {
      resetNewConfiguration(user);
      setScreen('flow');
    } else {
      openAuth('new');
    }
  };

  const resumeDraft = () => {
    if (user) {
      setScreen('flow');
    } else {
      openAuth('resume');
    }
  };

  const onAuthenticated = (nextUser: ConfiguratorUser) => {
    setUser(nextUser);
    window.localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    if (pendingAuthIntent === 'new') {
      resetNewConfiguration(nextUser);
    } else {
      setConfig(previous => ({
        ...previous,
        customerName: previous.customerName || nextUser.name,
        customerEmail: previous.customerEmail || nextUser.email,
        company: previous.company || nextUser.company || '',
      }));
    }
    setAuthOpen(false);
    if (pendingAuthIntent !== 'login') setScreen('flow');
    setPendingAuthIntent('login');
  };

  const selectStep = (step: number) => {
    if (!canEnterStep(step, config, outputs) || step === currentStep) return;
    changeStep(step);
  };

  const changeStep = (step: number) => {
    setSubmitError(null);
    setIsChanging(true);
    window.setTimeout(() => {
      startTransition(() => setCurrentStep(step));
      window.setTimeout(() => setIsChanging(false), 180);
    }, 120);
  };

  const continueFlow = () => {
    if (currentStep === FLOW_STEPS.length) {
      void submitConfiguration();
      return;
    }
    if (!canContinueStep(currentStep, config, outputs)) return;
    changeStep(Math.min(FLOW_STEPS.length, currentStep + 1));
  };

  const submitConfiguration = async () => {
    if (!canSubmit(config, outputs) || submissionInFlight.current) return;

    submissionInFlight.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 25_000);

    try {
      const response = await fetch('/api/configurator/submissions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(await prepareCaptchaBody({ configuration: config, outputs, locale }, CaptchaAction.CONFIGURATOR_SUBMIT)),
        signal: controller.signal,
      });
      const payload: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readSubmissionError(payload));
      }

      const nextReference = readSubmissionReference(payload);
      if (!nextReference) throw new Error('Server nevrátil referenčné číslo konfigurácie.');

      setReference(nextReference);
      setScreen('success');
      setDraftAvailable(false);
      window.localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('BDV configurator submission failed.', error);
      setSubmitError(
        error instanceof DOMException && error.name === 'AbortError'
          ? 'Odoslanie trvalo príliš dlho. Skontrolujte pripojenie a skúste to znova.'
          : error instanceof Error && error.message
            ? error.message
            : 'Konfiguráciu sa nepodarilo odoslať. Skúste to prosím znova.',
      );
    } finally {
      window.clearTimeout(timeout);
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const resetToLanding = () => {
    if (screen === 'flow') persistDraft(config, outputs, currentStep);
    setScreen('landing');
    setCurrentStep(1);
    setMaxVisitedStep(1);
  };

  const saveAndExit = () => {
    persistDraft(config, outputs, currentStep);
    setScreen('landing');
  };

  return (
    <main className={styles.appShell} data-screen={screen}>
      <ConfiguratorAuthDialog
        open={authOpen}
        onClose={() => {
          setAuthOpen(false);
          setPendingAuthIntent('login');
        }}
        onContinue={onAuthenticated}
      />

      <AppHeader
        screen={screen}
        user={user}
        onHome={resetToLanding}
        onLogin={() => openAuth('login')}
        onSaveExit={saveAndExit}
      />

      {screen === 'landing' && (
        <Landing
          user={user}
          draftAvailable={draftAvailable}
          config={config}
          estimate={estimate.total}
          onStart={startNew}
          onResume={resumeDraft}
          onLogin={() => openAuth('login')}
        />
      )}

      {screen === 'flow' && (
        <div className={styles.flowPage}>
          <FlowSidebar
            currentStep={currentStep}
            maxVisitedStep={maxVisitedStep}
            canSelect={step => canEnterStep(step, config, outputs)}
            onSelect={selectStep}
            mobileOpen={mobileStepsOpen}
            onMobileClose={() => setMobileStepsOpen(false)}
          />

          <div className={styles.flowMain}>
            <div className={styles.mobileFlowTopbar}>
              <button type="button" onClick={() => setMobileStepsOpen(true)}>
                <Menu size={18} /> Kroky
              </button>
              <span>
                {currentStep} / {FLOW_STEPS.length}
              </span>
              <strong>{FLOW_STEPS[currentStep - 1]?.label}</strong>
            </div>
            <div className={styles.mobileProgress}>
              <i style={{ width: `${progress}%` }} />
            </div>

            <div className={styles.stepContent}>
              {currentStep === 1 && <ProductStep config={config} update={update} />}
              {currentStep === 2 && <MachineStep config={config} update={update} />}
              {currentStep === 3 && (
                <ConfigurationStep config={config} update={update} estimate={estimate} />
              )}
              {currentStep === 4 && (
                <DocumentsStep
                  config={config}
                  estimate={estimate}
                  outputs={outputs}
                  setOutputs={setOutputs}
                />
              )}
              {currentStep === 5 && (
                <SendStep config={config} update={update} estimate={estimate} user={user} />
              )}
            </div>

            <FlowActions
              currentStep={currentStep}
              config={config}
              outputs={outputs}
              estimate={estimate}
              isChanging={isChanging}
              isSubmitting={isSubmitting}
              submitError={submitError}
              onBack={() =>
                currentStep === 1 ? setScreen('landing') : changeStep(currentStep - 1)
              }
              onContinue={continueFlow}
            />
          </div>

          {(isChanging || isSubmitting) && (
            <div className={styles.flowLoader} role="status" aria-live="polite">
              <span />
              {isSubmitting ? 'Odosielam konfiguráciu…' : 'Pripravujem ďalší krok…'}
            </div>
          )}
        </div>
      )}

      {screen === 'success' && (
        <SuccessScreen config={config} estimate={estimate} reference={reference} onNew={startNew} />
      )}
    </main>
  );
}

function limitHandleDepthForEditor(config: ConfiguratorState): ConfiguratorState {
  if (config.handle.depthMm <= HANDLE_DEPTH_CONTROL_MAX_MM) return config;
  return {
    ...config,
    handle: { ...config.handle, depthMm: HANDLE_DEPTH_CONTROL_MAX_MM },
  };
}

function AppHeader({
  screen,
  user,
  onHome,
  onLogin,
  onSaveExit,
}: {
  screen: Screen;
  user: ConfiguratorUser | null;
  onHome: () => void;
  onLogin: () => void;
  onSaveExit: () => void;
}) {
  return (
    <header className={styles.topbar}>
      <button type="button" className={styles.brand} onClick={onHome} aria-label="BDV domov">
        <Image
          className={styles.brandLogo}
          src="/images/branding/bdv-trade-logo.png"
          alt=""
          width={170}
          height={72}
          priority
          unoptimized
        />
        <i />
        <strong>Konfigurátor</strong>
      </button>
      <div className={styles.topbarCenter}>
        <span>Konfigurácia · Náhľad · Výroba</span>
      </div>
      <div className={styles.topbarActions}>
        {screen === 'flow' && (
          <button type="button" className={styles.saveExit} onClick={onSaveExit}>
            <Save size={16} />
            Uložiť a zavrieť
          </button>
        )}
        {user ? (
          <button type="button" className={styles.userButton} onClick={onLogin}>
            <CircleUserRound size={17} />
            <span>{user.name}</span>
            <i>{user.mode === 'account' ? 'Účet' : 'Hosť'}</i>
          </button>
        ) : (
          <button type="button" className={styles.loginButton} onClick={onLogin}>
            <LogIn size={16} /> Prihlásiť
          </button>
        )}
      </div>
    </header>
  );
}

function Landing({
  user,
  draftAvailable,
  config,
  estimate,
  onStart,
  onResume,
  onLogin,
}: {
  user: ConfiguratorUser | null;
  draftAvailable: boolean;
  config: ConfiguratorState;
  estimate: number;
  onStart: () => void;
  onResume: () => void;
  onLogin: () => void;
}) {
  const { t } = useTranslations();
  const draftMaterial = getMaterial(config.materialId);
  const heroMaterial = getMaterial(HERO_PREVIEW_CONFIG.materialId);
  return (
    <div className={styles.landing}>
      <section className={styles.hero}>
        <div className={styles.heroGrid} />
        <div className={styles.heroCopy}>
          <span className={styles.heroEyebrow}>
            <Sparkles size={15} /> Digitálny konfigurátor výroby
          </span>
          <h1>
            Od prvého parametra
            <em> až po výrobný súbor.</em>
          </h1>
          <p>
            Vyberte výrobok, nastavte jeho geometriu, materiál a opracovanie. Konfigurátor pripraví
            náhľad, cenu, výkres aj dáta pre CNC v jednom procese.
          </p>
          <div className={styles.heroActions}>
            <button type="button" className={styles.heroPrimary} onClick={onStart}>
              Začať konfiguráciu <ArrowRight size={18} />
            </button>
            {!user && (
              <button type="button" className={styles.heroSecondary} onClick={onLogin}>
                <CircleUserRound size={17} /> Mám účet
              </button>
            )}
          </div>
          <div className={styles.heroTrust}>
            <span>
              <Check size={14} /> Rôzne typy výrobkov
            </span>
            <span>
              <Check size={14} /> Živý 2D / 3D náhľad
            </span>
            <span>
              <Check size={14} /> Automatické výrobné výstupy
            </span>
          </div>
        </div>

        <div className={styles.heroObjectArea} aria-hidden="true">
          <div className={styles.heroOrbitOne} />
          <div className={styles.heroOrbitTwo} />
          <div className={styles.heroDoor}>
            <GeneratedDoorPreview
              config={HERO_PREVIEW_CONFIG}
              presentation="hero"
              className={styles.heroGeneratedPreview}
            />
          </div>
          <div className={styles.heroMaterialPill}>
            <i style={{ background: heroMaterial.color }} />
            <span>
              <small>{t('text4065')}</small>
              {heroMaterial.label}
            </span>
          </div>
          <div className={styles.heroGeometryPill}>
            <Rotate3D size={18} />
            <span>
              <small>Geometria</small>
              Plné 3D
            </span>
          </div>
        </div>
      </section>

      <section className={styles.landingBody}>
        {draftAvailable && (
          <button type="button" className={styles.draftBanner} onClick={onResume}>
            <span className={styles.draftIcon}>
              <FolderClock size={23} />
            </span>
            <span>
              <small>Rozpracovaná konfigurácia</small>
              <strong>
                {getProduct(config.productId).label} · {draftMaterial.label} · {config.widthMm} ×{' '}
                {config.heightMm} mm
              </strong>
              <i>Orientačne s DPH {formatCurrency(estimate)}</i>
            </span>
            <span className={styles.draftResume}>
              Pokračovať <ArrowRight size={17} />
            </span>
          </button>
        )}

        <div className={styles.processIntro}>
          <span className={styles.eyebrow}>Jeden súvislý proces</span>
          <h2>Od nápadu k výrobe bez prepisovania údajov.</h2>
          <p>
            Základ appky je pripravený tak, aby sa neskôr dopĺňali konkrétne výrobky a stroje, nie
            aby sa znovu skladal celý konfigurátor.
          </p>
        </div>

        <div className={styles.processGrid}>
          {[
            [
              '01',
              'Vyberiete výrobok',
              'Produktový modul určí dostupné parametre a geometriu.',
              PackageOpen,
            ],
            [
              '02',
              'Navrhnete ho v 3D',
              'Nepravidelné hrany a priestorové frézovanie vidíte okamžite.',
              Box,
            ],
            [
              '03',
              'Vzniknú dokumenty',
              'Ponuka, výrobný výkres a formáty pre ďalšie systémy.',
              FileOutput,
            ],
            [
              '04',
              'Odošlete na kontrolu',
              'Výroba dostane jeden konzistentný balík dát.',
              CloudCheck,
            ],
          ].map(([number, title, text, Icon]) => (
            <article key={number as string} className={styles.processCard}>
              <span>{number as string}</span>
              <Icon size={23} />
              <h3>{title as string}</h3>
              <p>{text as string}</p>
            </article>
          ))}
        </div>

        <div className={styles.materialShowcase}>
          <div>
            <span className={styles.eyebrowLight}>Materiálová knižnica</span>
            <h2>Pripravené pre drevo, plasty aj mäkké kovy.</h2>
          </div>
          <div className={styles.materialSwatches}>
            {['Drevo', 'Plast', 'Zinok', 'Hliník'].map((label, index) => (
              <span key={label}>
                <i data-index={index} />
                <strong>{label}</strong>
              </span>
            ))}
          </div>
          <button type="button" onClick={onStart}>
            Otvoriť konfigurátor <ChevronRight size={18} />
          </button>
        </div>
      </section>
    </div>
  );
}

function FlowSidebar({
  currentStep,
  maxVisitedStep,
  canSelect,
  onSelect,
  mobileOpen,
  onMobileClose,
}: {
  currentStep: number;
  maxVisitedStep: number;
  canSelect: (step: number) => boolean;
  onSelect: (step: number) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}) {
  const { t } = useTranslations();

  return (
    <aside
      className={`${styles.flowSidebar} ${mobileOpen ? styles.flowSidebarOpen : ''}`}
      data-mobile-open={mobileOpen ? 'true' : 'false'}
    >
      <button
        type="button"
        className={styles.mobileSidebarClose}
        onClick={onMobileClose}
        aria-label="Zavrieť kroky konfigurácie"
      >
        <X size={19} />
      </button>
      <div className={styles.sidebarIntro}>
        <span className={styles.eyebrowLight}>{t('text4066')}</span>
        <h2>Dvierka s integrovaným úchytom</h2>
        <p>Postupujte krok za krokom. Rozpracovaný návrh sa priebežne ukladá.</p>
      </div>
      <nav className={styles.stepper} aria-label={t('text4067')}>
        {FLOW_STEPS.map(step => {
          const done =
            step.id < maxVisitedStep && canSelect(Math.min(FLOW_STEPS.length, step.id + 1));
          const active = currentStep === step.id;
          const reachable = canSelect(step.id);
          const Icon = step.icon;
          return (
            <button
              type="button"
              key={step.id}
              disabled={!reachable}
              aria-current={active ? 'step' : undefined}
              onClick={() => {
                onSelect(step.id);
                onMobileClose();
              }}
              className={active ? styles.stepActive : done ? styles.stepDone : styles.step}
            >
              <span>{done ? <Check size={16} /> : <Icon size={17} />}</span>
              <i>0{step.id}</i>
              <strong>{step.label}</strong>
              {active && <ChevronRight size={16} />}
            </button>
          );
        })}
      </nav>
      <div className={styles.sidebarStatus}>
        <CloudCheck size={18} />
        <span>
          <strong>Automaticky uložené</strong>
          Lokálne v tomto zariadení
        </span>
      </div>
    </aside>
  );
}

function FlowActions({
  currentStep,
  config,
  outputs,
  estimate,
  isChanging,
  isSubmitting,
  submitError,
  onBack,
  onContinue,
}: {
  currentStep: number;
  config: ConfiguratorState;
  outputs: GeneratedOutputs;
  estimate: PriceEstimate;
  isChanging: boolean;
  isSubmitting: boolean;
  submitError: string | null;
  onBack: () => void;
  onContinue: () => void;
}) {
  const allowed = canContinueStep(currentStep, config, outputs);
  const hint = getContinueHint(currentStep, config, outputs);
  return (
    <footer className={styles.flowActions}>
      <button type="button" className={styles.backButton} onClick={onBack} disabled={isChanging}>
        <ArrowLeft size={17} />
        {currentStep === 1 ? 'Späť na úvod' : 'Predchádzajúci krok'}
      </button>
      <div className={styles.flowActionSummary}>
        <span>Orientačne spolu s DPH</span>
        <strong>{formatCurrency(estimate.total)}</strong>
        {config.quantity > 1 && <small>{formatCurrency(estimate.unitPrice)} / ks s DPH</small>}
      </div>
      <div className={styles.continueWrap}>
        {submitError ? (
          <span className={styles.submitError} role="alert">
            {submitError}
          </span>
        ) : (
          !allowed && <span className={styles.continueHint}>{hint}</span>
        )}
        <button
          type="button"
          className={styles.continueButton}
          onClick={onContinue}
          disabled={!allowed || isChanging || isSubmitting}
        >
          {currentStep === FLOW_STEPS.length ? (
            <>
              {isSubmitting ? 'Odosielam…' : 'Odoslať konfiguráciu'} <Send size={17} />
            </>
          ) : (
            <>
              Pokračovať <ArrowRight size={17} />
            </>
          )}
        </button>
      </div>
    </footer>
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readSubmissionReference(payload: unknown) {
  if (!isRecord(payload)) return '';
  for (const key of ['reference', 'submissionReference', 'token', 'identifier', 'id']) {
    const value = payload[key];
    if ((typeof value === 'string' || typeof value === 'number') && String(value).trim()) {
      return String(value).trim();
    }
  }
  return readSubmissionReference(payload.data);
}

function readSubmissionError(payload: unknown) {
  if (!isRecord(payload)) return 'Konfiguráciu sa nepodarilo odoslať. Skúste to prosím znova.';
  const message = payload.message;
  return typeof message === 'string' && message.trim()
    ? message.trim()
    : 'Konfiguráciu sa nepodarilo odoslať. Skúste to prosím znova.';
}
